import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LucideCheck, LucideChevronDown, LucideChevronRight, LucideChevronUp } from '@lucide/angular';
import { Subscription } from 'rxjs';
import { ScheduleType } from '../activities/activity-type.model';
import { DayStatusCardComponent } from './day-status-card.component';
import { DayStore } from './day.store';
import { getDaysSinceAriaLabel } from './day-status-cycles.util';
import { UpNextActivity, buildUpNextMetaLabel, resolveUpNextActivity } from './group-page.util';
import { rebuildOrderedIdsForSubset } from './order.util';
import { groupPageCommands, scheduleTypeFromSlug } from './schedule-group.util';

/**
 * Component for displaying today's activities, including time blocks and habit counters.
 *
 * This component allows users to view their allocated time blocks and habits for the current day,
 * increment habit counters, and start or resume allocated time blocks.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-today',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DayStatusCardComponent,
    CdkDropList,
    CdkDrag,
    LucideChevronRight,
    LucideCheck,
    LucideChevronDown,
    LucideChevronUp
  ],
  templateUrl: './today.component.html',
  styleUrls: ['./today.component.scss'],
  animations: [
    trigger('habitGoalFoldMotion', [
      transition(':leave', [
        animate('{{duration}}ms ease', style({ opacity: 0, transform: 'translateY(-4px)' }))
      ], { params: { duration: 180 } })
    ])
  ]
})
export class TodayComponent implements OnInit, OnDestroy {
  private readonly store = inject(DayStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly day = this.store.day;
  readonly dayLoading = this.store.dayLoading;
  readonly dayError = this.store.dayError;
  readonly habitSubmitting = this.store.habitSubmitting;
  readonly activityReorderSubmitting = this.store.activityReorderSubmitting;
  readonly activityReorderError = this.store.activityReorderError;
  readonly reorderSubmitting = this.store.reorderSubmitting;
  readonly celebrationCycle = this.store.celebrationCycle;
  readonly celebrationSummary = this.store.celebrationSummary;
  readonly lastCompletedWorkCycle = this.store.lastCompletedWorkCycle;
  readonly lastCompletedLifeCycle = this.store.lastCompletedLifeCycle;
  readonly dayStatus = this.store.dayStatus;
  readonly dayStatusCycles = this.store.dayStatusCycles;
  readonly habitRows = this.store.habitRows;
  readonly listDragStartDelay = { touch: 160, mouse: 0 };
  readonly doneHabitsListId = 'today-done-habits-list';
  readonly doneHabitsExpanded = signal(false);
  readonly prefersReducedMotion = signal(false);
  readonly habitMotionDurationMs = computed(() => (this.prefersReducedMotion() ? 0 : 180));
  private readonly doneHabitsExpandedPreferenceKey = 'lumen.today.doneHabitsExpanded';

  private routeQueryParamSubscription: Subscription | null = null;
  readonly openHabitRows = computed(() => this.habitRows().filter((row) => !row.isCompleted));
  readonly doneHabitRows = computed(() => this.habitRows().filter((row) => row.isCompleted));
  readonly habitsDoneCount = computed(() => this.doneHabitRows().length);
  readonly habitsTotalCount = computed(() => this.habitRows().length);
  readonly habitsHeading = computed(() => (
    this.habitsTotalCount() > 0
      ? `Habits · ${this.habitsDoneCount()} of ${this.habitsTotalCount()} done`
      : 'Habits'
  ));
  readonly upNext = computed(() => resolveUpNextActivity(
    this.dayStatusCycles(),
    (ordinal) => this.store.getAllocationRowsForScheduleType(ordinal),
    this.store.dayStatusOldestFirst()
  ));
  readonly allGroupsDone = computed(() => {
    const cycles = this.dayStatusCycles();
    const hasGroups = cycles.work.groups.length > 0 || cycles.life.groups.length > 0;
    return hasGroups && this.upNext() === null;
  });

  ngOnInit(): void {
    this.applyLegacyTabFromQuery(this.route.snapshot.queryParamMap.get('tab'));
    this.routeQueryParamSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.applyLegacyTabFromQuery(queryParams.get('tab'));
    });
    this.doneHabitsExpanded.set(this.readDoneHabitsExpandedPreference());
    this.prefersReducedMotion.set(this.resolvePrefersReducedMotion());
    this.store.ensureLoaded().subscribe();
  }

  ngOnDestroy(): void {
    this.routeQueryParamSubscription?.unsubscribe();
  }

  incrementHabit(activityTypeId: string): void {
    this.store.incrementHabit(activityTypeId);
  }

  reorderGrouping(enabledOrdinals: ScheduleType[]): void {
    this.store.reorderGrouping(enabledOrdinals);
  }

  isHabitSubmitting(activityTypeId: string): boolean {
    return !!this.habitSubmitting()[activityTypeId];
  }

  getHabitAriaLabel(habit: {
    name: string;
    total: number;
    target: number | null;
    isCompleted: boolean;
    openTaskCount: number;
    hasOpenTasks: boolean;
    daysSince?: number | null;
  }): string {
    const openTaskSuffix = habit.hasOpenTasks
      ? ` ${habit.openTaskCount} open task${habit.openTaskCount === 1 ? '' : 's'}.`
      : ' No open tasks.';
    const daysSinceSuffix = habit.daysSince !== undefined
      ? ` ${capitalize(getDaysSinceAriaLabel(habit.daysSince))}.`
      : '';
    if (habit.target === null) {
      return `${habit.name}, no daily target, total ${habit.total}.${openTaskSuffix}${daysSinceSuffix}`;
    }
    const status = habit.isCompleted ? 'completed' : 'in progress';
    return `${habit.name}, ${status}, ${habit.total} of ${habit.target} daily target.${openTaskSuffix}${daysSinceSuffix}`;
  }

  refreshDayError(): void {
    this.store.refreshDayError();
  }

  refreshAllocationError(): void {
    this.store.refreshAllocationError();
  }

  onHabitDrop(event: CdkDragDrop<unknown[]>): void {
    if (this.activityReorderSubmitting() || event.previousIndex === event.currentIndex) {
      return;
    }
    const orderedIds = this.habitRows().map((habit) => habit.activityTypeId);
    const previousOpenIds = this.openHabitRows().map((habit) => habit.activityTypeId);
    const reorderedOpenIds = previousOpenIds.slice();
    moveItemInArray(reorderedOpenIds, event.previousIndex, event.currentIndex);
    const nextOrderedIds = rebuildOrderedIdsForSubset(orderedIds, previousOpenIds, reorderedOpenIds);
    this.store.persistActivityOrder(nextOrderedIds, { kind: 'HABIT_COUNTER' }, 'Failed to reorder habits');
  }

  toggleDoneHabitsExpanded(): void {
    const next = !this.doneHabitsExpanded();
    this.doneHabitsExpanded.set(next);
    this.writeDoneHabitsExpandedPreference(next);
  }

  getHabitMotionAnimation() {
    return { value: '', params: { duration: this.habitMotionDurationMs() } };
  }

  isDoneHabitsExpanded(): boolean {
    return this.doneHabitsExpanded();
  }

  doneHabitsCount(): number {
    return this.doneHabitRows().length;
  }

  private writeDoneHabitsExpandedPreference(expanded: boolean): void {
    try {
      localStorage.setItem(this.doneHabitsExpandedPreferenceKey, expanded ? 'true' : 'false');
    } catch {
      // Ignore localStorage access failures.
    }
  }

  private readDoneHabitsExpandedPreference(): boolean {
    try {
      return localStorage.getItem(this.doneHabitsExpandedPreferenceKey) === 'true';
    } catch {
      return false;
    }
  }

  private resolvePrefersReducedMotion(): boolean {
    try {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
      }
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }

  navigateToHabit(activityTypeId: string): void {
    this.router.navigate(['/today/habit', activityTypeId], {
      queryParams: { from: 'habits' }
    });
  }

  onDayStatusGroupSelect(scheduleType: ScheduleType): void {
    this.router.navigate(groupPageCommands(scheduleType));
  }

  trackHabit(_index: number, habit: { activityTypeId: string }): string {
    return habit.activityTypeId;
  }

  getUpNextDaysAriaLabel(daysSince: number | null): string {
    return getDaysSinceAriaLabel(daysSince);
  }

  upNextMetaLabel(upNext: UpNextActivity): string {
    return buildUpNextMetaLabel(upNext);
  }

  toDisplayDateForDayDate(dayDate: string): Date {
    const [yearPart, monthPart, dayPart] = dayDate.split('-');
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return new Date(dayDate);
    }
    // Construct a browser-local Date corresponding to the given calendar day.
    // The template intentionally formats this without an explicit timezone so
    // construction + formatting use the same zone.
    return new Date(year, month - 1, day);
  }

  private applyLegacyTabFromQuery(tab: string | null): void {
    if (!tab) {
      return;
    }
    const scheduleType = scheduleTypeFromSlug(tab);
    if (scheduleType) {
      this.router.navigate(groupPageCommands(scheduleType), {
        replaceUrl: true,
        queryParams: { tab: null },
        queryParamsHandling: 'merge'
      });
      return;
    }
    const normalized = tab.trim().toLowerCase();
    if (normalized === 'habits' || normalized === 'logged' || normalized === 'hidden') {
      this.router.navigate([], {
        relativeTo: this.route,
        replaceUrl: true,
        queryParams: { tab: null },
        queryParamsHandling: 'merge'
      });
    }
  }
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
